/**
 * PDF Template Client using tRPC
 *
 * This client connects to the backend API via tRPC to manage PDF templates.
 * Uses the kernel-plugin-pdf router endpoints.
 */

import type { BlockTemplate as EditorBlockTemplate } from '@xhubio-saas/pdf-editor-frontend'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

/**
 * Vorbelegung einer NEUEN Vorlage aus dem Maßsystem des Betriebs
 * (Plan 16, Schritt 3).
 *
 * | Org-Maßsystem | `lengthUnit` | `page.size` | Ränder    |
 * |---------------|--------------|-------------|-----------|
 * | `metric`      | `mm`         | `A4`        | 20 mm     |
 * | `imperial`    | `inch`       | `LETTER`    | 0,75 Zoll |
 *
 * 🔴 Die Organisation ist **Vorbelegung, nicht Auflösung**. Beide Werte stehen
 * danach an der Vorlage und reisen mit ihr: stellt der Betrieb später auf
 * imperial um, bleibt eine bestehende A4-Vorlage A4. Umgekehrt darf der
 * deutsche Betrieb, der einen Report für einen Amerikaner baut, genau diese
 * eine Vorlage auf LETTER stellen, ohne die Organisation anzufassen. Hinge die
 * Deutung am Betrachter, ergäbe dieselbe Datei in zwei Betrieben zwei
 * verschiedene PDFs.
 *
 * 🔴 Warum ein **vollständiges** v2-Shape und nicht bloß die zwei Felder: die
 * Editor-Seite erkennt eine Vorlage an `version` + `body`. Ein Objekt mit nur
 * `{ lengthUnit, page }` ist weder v2-Shape noch leer und fällt in den
 * Legacy-Zweig — der Sackgassen-Screen stünde direkt nach dem Anlegen.
 *
 * 🔴 Warum die Ränder mit der Einheit wechseln: `convertTemplateSpatialToPt`
 * rechnet `page.margins` mit `lengthUnit` um. Eine `20` wäre bei `inch`
 * 20 Zoll = 1440 pt und sprengt jede Seite. 20 mm (56,7 pt) und 0,75 Zoll
 * (54 pt) liegen dagegen beieinander. Im metrischen Fall ist das Ergebnis
 * deckungsgleich mit dem bisherigen Verhalten — eine Vorlage ohne `lengthUnit`
 * wurde beim Laden ohnehin als mm aufgelöst.
 */
export function starterTemplate(
  name: string,
  measurementSystem: 'metric' | 'imperial'
): EditorBlockTemplate {
  const imperial = measurementSystem === 'imperial'
  return {
    version: '1.0.0',
    name,
    page: {
      size: imperial ? 'LETTER' : 'A4',
      orientation: 'portrait',
      margins: imperial
        ? { top: 0.75, right: 0.75, bottom: 0.75, left: 0.75 }
        : { top: 20, right: 20, bottom: 20, left: 20 },
    },
    body: { blocks: [] },
    editorVersion: 'v2',
    lengthUnit: imperial ? 'inch' : 'mm',
  }
}

/** tRPC delivers timestamps as string or Date depending on the transformer. */
function toIsoString(value: string | Date): string {
  return typeof value === 'string' ? value : value.toISOString()
}

/**
 * Create template input
 */
export interface CreateTemplateInput {
  name: string
  description?: string
  type: string
}

/**
 * One row of the template list as `loadProjectList` delivers it.
 */
export interface PdfTemplateListEntry {
  id: string
  name: string
  description?: string
  createdAt: string
  updatedAt: string
  /**
   * System template (from `config/pdf-templates/`, read-only on the server:
   * `SystemTemplateReadOnlyError`). `TemplateListPage` hides "Edit"/"Delete"
   * for it and shows the "Readonly" badge; "Duplicate" stays the way out.
   * 🔴 Dropping this field here offered 42 system templates for deletion on
   * PROD (Issue API 2026-10-20). Parity: saas-pdf-api-frontend.
   */
  isSystem: boolean
  // Surfaced by pdf-backend >=2.37 as a synthetic column on
  // templateList rows — drives the v1/v2 badge + the legacy
  // editor's row-disable behaviour.
  editorVersion?: 'v1' | 'v2'
  // Free-form document type so the V2 list's create dialog can
  // suggest types the org already uses ("Rechnung", "Angebot",
  // …). Pre-existing column on the templates table.
  documentType?: string
}

/**
 * PDF Template Client interface
 */
export interface PdfTemplateClient {
  loadProjectList: () => Promise<
    { success: true; projects: PdfTemplateListEntry[] } | { success: false; error: string }
  >

  deleteTemplate: (id: string) => Promise<{ success: true } | { success: false; error: string }>

  duplicateTemplate: (
    id: string
  ) => Promise<{ success: true; newId: string } | { success: false; error: string }>
}

/**
 * Creates a PDF Template client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns PdfTemplateClient implementation using tRPC
 */
export function usePdfTemplateClient(): PdfTemplateClient {
  const utils = trpc.useUtils()

  // Get mutation functions - using correct procedure names from kernel-plugin-pdf
  const deleteMutation = trpc.pdf.templateDelete.useMutation()
  const duplicateMutation = trpc.pdf.templateDuplicate.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    deleteMutation,
    duplicateMutation,
  })
  mutations.current = {
    deleteMutation,
    duplicateMutation,
  }

  // Das Suffix des Duplikats kommt aus dem Katalog des `pdf`-Namensraums
  // (`templates.copySuffix`, zwoelf Sprachen) — es wandert aus demselben Grund
  // wie die Mutationen in eine Ref: `t` wechselt beim Sprachwechsel die
  // Identitaet und duerfte sonst die Client-Identitaet mitreissen. Gelesen wird
  // es nur im Callback, nie waehrend des Renderns.
  // 🔴 Kein englischer Ersatztext im Code: der Schluessel steht in ALLEN zwoelf
  // Katalogen (Waechter `pdf-frontend/tests/locales-fuehren-jeden-schluessel.test.ts`),
  // und die i18next-Kette faellt auf die Fallback-Sprache zurueck, nicht auf ein
  // Literal. Ein englisches Ersatz-Suffix hier waere genau das Wort, das dieser
  // Plan aus der deutschen Oberflaeche nimmt — und das Fertig-Kriterium des Plans
  // misst, dass es in dieser Datei nicht mehr steht.
  const { t } = useTranslation('pdf')
  const copySuffixRef = useRef(t('templates.copySuffix'))
  copySuffixRef.current = t('templates.copySuffix')

  return useMemo(
    () => ({
      async loadProjectList(): Promise<
        { success: true; projects: PdfTemplateListEntry[] } | { success: false; error: string }
      > {
        try {
          // Invalidate cache to ensure fresh data
          await utils.pdf.templateList.invalidate()
          const result = await utils.pdf.templateList.fetch({
            limit: 100,
            offset: 0,
            includeInactive: false,
          })

          const projects = (result.templates || []).map(
            (t: {
              id: string
              name: string
              description?: string | null
              createdAt: string | Date
              updatedAt: string | Date
              isSystem: boolean
              documentType?: string | null
              editorVersion?: 'v1' | 'v2' | null
            }): PdfTemplateListEntry => ({
              id: t.id,
              name: t.name,
              description: t.description ?? undefined,
              createdAt: toIsoString(t.createdAt),
              updatedAt: toIsoString(t.updatedAt),
              isSystem: t.isSystem,
              documentType: t.documentType ?? undefined,
              editorVersion: t.editorVersion ?? undefined,
            })
          )

          return { success: true, projects }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load project list'
          // Detect plan upgrade required - not a system error, just a plan limitation
          const isPlanUpgrade = message.includes('Plan upgrade required')
          return {
            success: false,
            error: isPlanUpgrade ? 'PLAN_UPGRADE_REQUIRED' : message,
          }
        }
      },

      async deleteTemplate(
        id: string
      ): Promise<{ success: true } | { success: false; error: string }> {
        try {
          await mutations.current.deleteMutation.mutateAsync({ id, permanent: false })
          // Invalidate cache so list will refetch
          await utils.pdf.templateList.invalidate()
          return { success: true }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to delete template',
          }
        }
      },

      async duplicateTemplate(
        id: string
      ): Promise<{ success: true; newId: string } | { success: false; error: string }> {
        try {
          // First load the template to duplicate
          const template = await utils.pdf.templateGet.fetch({ id })

          if (!template) {
            return { success: false, error: 'Template not found' }
          }

          // Kopiert wird auf dem SERVER: `templateDuplicate` nimmt die Id und
          // den neuen Namen. Vorher baute der Browser die Kopie selbst aus dem
          // geladenen Datensatz zusammen (name, template, pageSettings,
          // sampleData, isDefault) — ein Rundweg mit zwei Schaeden:
          //
          //   🔴 `sampleData` ist bei jeder ueber den Anlege-Dialog erzeugten
          //      Vorlage `null` (`handleCreateTemplate` in `app.pages.tsx`
          //      schickt das Feld gar nicht mit), waehrend `templateCreate` es
          //      als `z.record(...).optional()` fuehrt — `.optional()` laesst
          //      `undefined` zu, aber NICHT `null`. JEDES Duplizieren einer
          //      solchen Vorlage endete deshalb in HTTP 400 mit rohem Zod-JSON
          //      im Toast (Lane Qv an verein gemessen, hier App-Paritaet).
          //   🔴 `documentType` stand gar nicht in der Nutzlast — die Kopie
          //      waere still zur RECHNUNGS-Vorlage geworden
          //      (`z.string().default('invoice')`), obwohl der Anlege-Dialog
          //      dieser App den Typ als freies Feld fuehrt.
          //
          // Beides faellt weg, sobald der Server kopiert: er uebernimmt
          // `description`, `documentType`, `template`, `sampleData` und
          // `pageSettings` direkt aus der Quellzeile. Dieselbe Prozedur fahren
          // `saas-dashandwerk-frontend` und `saas-pdf-api-frontend` bereits.
          // Regel: `.claude/rules/browser-schickt-nichts-zurueck.md`.
          const result = await mutations.current.duplicateMutation.mutateAsync({
            id,
            newName: `${template.name}${copySuffixRef.current}`,
          })

          // Invalidate cache so list will refetch
          await utils.pdf.templateList.invalidate()
          return { success: true, newId: result.id }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to duplicate template',
          }
        }
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}
